import { Loader2 } from "lucide-react";

export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-6 fade-up">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">{title}</h1>
        {subtitle && <p className="text-slate-500 text-sm mt-1">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

export function StatCard({ label, value, sub, icon: Icon, tone = "navy", testid }) {
  const tones = {
    navy: "from-[#0F2C59] to-[#00529B] text-white",
    gold: "from-[#D97706] to-[#F59E0B] text-white",
    emerald: "from-[#047857] to-[#059669] text-white",
    violet: "from-[#6D28D9] to-[#7C3AED] text-white",
    crimson: "from-[#B91C1C] to-[#DC2626] text-white",
    white: "bg-white text-slate-900 border border-slate-200",
  };
  const gradient = tone !== "white" ? `bg-gradient-to-br ${tones[tone]}` : tones.white;
  const muted = tone !== "white";
  return (
    <div data-testid={testid} className={`kpi-card rounded-xl p-5 ${gradient} ${tone === "white" ? "shadow-sm" : "shadow-md"}`}>
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <div className={`text-[11px] uppercase tracking-wider font-semibold ${muted ? "text-white/70" : "text-slate-400"}`}>{label}</div>
          <div className="font-heading text-2xl font-bold mt-1.5 truncate">{value}</div>
          {sub && <div className={`text-xs mt-1 ${muted ? "text-white/70" : "text-slate-400"}`}>{sub}</div>}
        </div>
        {Icon && (
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${muted ? "bg-white/15" : "bg-slate-100 text-[#00529B]"}`}>
            <Icon size={20} />
          </div>
        )}
      </div>
    </div>
  );
}

export function Panel({ title, subtitle, children, actions, className = "", testid }) {
  return (
    <div data-testid={testid} className={`bg-white rounded-xl border border-slate-200 shadow-sm ${className}`}>
      {(title || actions) && (
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div>
            {title && <h3 className="font-heading font-semibold text-slate-800">{title}</h3>}
            {subtitle && <p className="text-xs text-slate-400 mt-0.5">{subtitle}</p>}
          </div>
          {actions}
        </div>
      )}
      <div className="p-5">{children}</div>
    </div>
  );
}

export function Loading({ label = "Loading…" }) {
  return (
    <div className="flex items-center justify-center py-24 text-slate-400 gap-2">
      <Loader2 className="animate-spin" size={18} /> {label}
    </div>
  );
}

const badgeTones = {
  active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  settled: "bg-emerald-50 text-emerald-700 border-emerald-200",
  synced: "bg-emerald-50 text-emerald-700 border-emerald-200",
  resolved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  passed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  investigating: "bg-amber-50 text-amber-700 border-amber-200",
  hold: "bg-amber-50 text-amber-700 border-amber-200",
  draft: "bg-slate-100 text-slate-600 border-slate-200",
  inactive: "bg-slate-100 text-slate-500 border-slate-200",
  new: "bg-blue-50 text-blue-700 border-blue-200",
  revival: "bg-violet-50 text-violet-700 border-violet-200",
  open: "bg-red-50 text-red-700 border-red-200",
  rejected: "bg-red-50 text-red-700 border-red-200",
  invalid: "bg-red-50 text-red-700 border-red-200",
  duplicate: "bg-orange-50 text-orange-700 border-orange-200",
  high: "bg-red-50 text-red-700 border-red-200",
  medium: "bg-amber-50 text-amber-700 border-amber-200",
  low: "bg-slate-100 text-slate-600 border-slate-200",
  not_posted: "bg-red-50 text-red-700 border-red-200",
};

export function Pill({ children, tone, testid }) {
  const key = String(tone || children || "").toLowerCase();
  const cls = badgeTones[key] || "bg-slate-100 text-slate-600 border-slate-200";
  return (
    <span data-testid={testid} className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold border ${cls}`}>
      {children}
    </span>
  );
}

export const CHART_COLORS = ["#00529B", "#00A3E0", "#D97706", "#059669", "#7C3AED", "#DC2626", "#2563EB", "#10B981"];
